import {
  addDownloadButtonToChart,
  displayNoDataOverlay,
  getChartContainerDimensions,
} from './charts_utils.js';

/**
 * One tooltip element per class, reused across renders.
 *
 * The chart re-renders on every resize, and each render used to append a fresh
 * div to <body>; dragging a window edge for a couple of seconds left hundreds
 * of orphaned tooltips in the document.
 *
 * @param {string} className - The tooltip's class, also its identity.
 * @returns {object} A d3 selection of the single node for that class.
 */
function reuseTooltip(className) {
  const existing = d3.select("body").select(`div.${className}`);
  const node = existing.empty()
    ? d3.select("body").append("div").attr("class", className)
    : existing;
  return node.style("opacity", 0);
}

/**
 * Earliest year the time series will display.
 *
 * Isolation dates in the data reach back to 1678, and ~1,200 records sit at exactly
 * 1900 as a placeholder rather than a real date. Left alone, the d3.min() below picks
 * up 1678, so the slider spans three and a half centuries to present a range that is
 * 97% post-1990 -- which makes the part anyone cares about unusable.
 *
 * 1950 discards the impossible dates and the 1900 placeholder spike (~2,450 records,
 * under 1% of dated genomes) while keeping genuine mid-century culture-collection
 * strains, which do legitimately date from the 1950s onward.
 */
export const MIN_DISPLAY_YEAR = 1950;

export function timeSeriesPlot(containerID, dataYear, dataMonth, downloadable = true) {
  // filter year and month by current date. Max <= current date
  const current_date = new Date();
  const min_date = new Date(MIN_DISPLAY_YEAR, 0, 1);
  const inDisplayRange = (d) =>
    d.Date &&
    !isNaN(d.Date) &&
    d.Date >= min_date &&
    d.Date <= current_date &&
    isFinite(+d.Count);

  dataYear = (dataYear || []).filter(inDisplayRange);
  dataMonth = (dataMonth || []).filter(inDisplayRange);

  if (!dataYear.length && !dataMonth.length) {
    displayNoDataOverlay(containerID, "No time series data available");
    return;
  }

  const graphContainer = d3.select("#" + containerID);
  graphContainer.selectAll("*").remove();
  graphContainer.innerHTML = "";

  graphContainer
    .style("max-width", null)
    .style("width", null)
    .style("display", null)
    .style("position", null)
    .style("overflow", "hidden");

  // This used to be guarded with `typeof getChartContainerDimensions ===
  // "function"`, and the name was never imported, so the guard was always false
  // and the chart rendered at the 960x420 fallback no matter how much room the
  // panel had. That is what made it read small. It is a static import now, so
  // a missing export fails at load instead of silently degrading.
  const { width: outerWidth, height: outerHeight } = getChartContainerDimensions(
    containerID,
    960,
    420,
  );

  // Now, append the SVG element
  const svg = graphContainer
    .append("svg")
    .attr("id", "my_dataviz")
    .attr("viewBox", `0 0 ${outerWidth} ${outerHeight}`)
    .attr("width", outerWidth)
    .attr("height", outerHeight)
    .style("width", "100%")
    .style("height", `${outerHeight}px`)
    .style("border", "0");

  // Chrome scales with the panel instead of being fixed at a size tuned for a
  // 960x420 box: at full width the axis type stayed 14px and the plot kept the
  // same 500px-tall proportions, which is what made the chart read small.
  const typeScale = Math.max(1, Math.min(1.5, outerHeight / 460));
  const axisFontPx = Math.round(13 * typeScale);
  const labelFontPx = Math.round(14 * typeScale);

  const sliderReserved = Math.round(72 * typeScale);
  const margin = {
    top: Math.round(18 * typeScale),
    right: Math.round(76 * typeScale),
    bottom: Math.round(52 * typeScale),
    left: Math.round(28 * typeScale),
  };
  const width = Math.max(220, outerWidth - margin.left - margin.right);
  const height = Math.max(
    140,
    outerHeight - margin.top - margin.bottom - sliderReserved,
  );

  // More room means more useful ticks, not just bigger ones.
  const xTickCount = Math.max(4, Math.min(16, Math.round(width / 110)));
  const yTickCount = Math.max(4, Math.min(12, Math.round(height / 55)));

  // Convert dates to years for the slider
  const years = dataYear.map((d) => d.Date.getFullYear());
  const minYear = d3.min(years);
  const maxYear = d3.max(years);

  // Set up scales
  const xTimeAxis = d3
    .scaleTime()
    .domain([new Date(minYear, 0, 1), new Date(maxYear, 0, 1)])
    .range([0, width]);

  const y = d3.scaleLinear().range([height, 0]);

  var toggleYear = true;

  // Create the SVG element and append it to the chart container
  const g = svg
    .append("g")
    .attr("transform", `translate(${margin.left},${margin.top})`);

  // Create the tooltips
  const tooltip = reuseTooltip("tooltip-timechart");
  const tooltipRawDate = reuseTooltip("tooltip-raw-date");

  const gradient = g
    .append("defs")
    .append("linearGradient")
    .attr("id", "gradient")
    .attr("x1", "0%")
    .attr("x2", "0%")
    .attr("y1", "0%")
    .attr("y2", "100%")
    .attr("spreadMethod", "pad");

  gradient
    .append("stop")
    .attr("offset", "0%")
    .attr("stop-color", "#55caff")
    .attr("stop-opacity", 1);

  gradient
    .append("stop")
    .attr("offset", "100%")
    .attr("stop-color", "#55caff")
    .attr("stop-opacity", 0);

  xTimeAxis.domain([new Date(minYear, 0, 1), new Date(maxYear, 0, 1)]);
  y.domain([0, d3.max(dataYear, (d) => d.Count)]);

  g.append("g")
    .attr("class", "x-axis")
    .attr("transform", `translate(0,${height})`)
    .style("font-size", `${axisFontPx}px`)
    .call(
      d3.axisBottom(xTimeAxis).ticks(xTickCount).tickFormat(d3.timeFormat("%Y")),
    )
    .selectAll(".tick line")
    .style("stroke-opacity", 1);

  g.append("g")
    .attr("class", "y-axis")
    .attr("transform", `translate(${width},0)`)
    .style("font-size", `${axisFontPx}px`)
    .call(
      d3
        .axisRight(y)
        .ticks(yTickCount)
        .tickFormat((d) => (d > 0 ? d : "")),
    );

  const line = d3
    .line()
    .x((d) => xTimeAxis(d.Date))
    .y((d) => y(d.Count));

  const area = d3
    .area()
    .x((d) => xTimeAxis(d.Date))
    .y0(height)
    .y1((d) => y(d.Count));

  g.append("path")
    .datum(dataYear)
    .attr("class", "area")
    .attr("d", area)
    .style("fill", "url(#gradient)")
    .style("opacity", 0.5);

  g.append("path")
    .datum(dataYear)
    .attr("class", "line")
    .attr("fill", "none")
    .attr("stroke", "#55caff")
    .attr("stroke-width", Math.max(2, Math.round(2 * typeScale)))
    .attr("d", line);

  const axisLabel = g
    .append("text")
    .attr("transform", `translate(${width / 2},${height + margin.bottom - 12})`)
    .style("text-anchor", "middle")
    .style("font-size", `${labelFontPx}px`)
    .text("Year");

  g.append("text")
    .attr(
      "transform",
      `translate(${width + margin.right - 22},${height / 2}) rotate(-90)`,
    )
    .attr("dy", "1em")
    .style("text-anchor", "middle")
    .style("font-size", `${labelFontPx}px`)
    .text("Number of genomes");

  const circle = g
    .append("circle")
    .attr("r", 0)
    .attr("fill", "red")
    .style("stroke", "var(--color-surface, white)")
    .attr("opacity", 0.7)
    .style("pointer-events", "none");

  const tooltipLineX = g
    .append("line")
    .attr("class", "tooltip-line")
    .attr("id", "tooltip-line-x")
    .attr("stroke", "red")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "2,2");

  const tooltipLineY = g
    .append("line")
    .attr("class", "tooltip-line")
    .attr("id", "tooltip-line-y")
    .attr("stroke", "red")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "2,2");

  const listeningRect = g
    .append("rect")
    .attr("width", width)
    .attr("height", height)
    .style("fill", "none")
    .style("pointer-events", "all");

  listeningRect.on("mousemove", function (event) {
    const [xCoord, yCoord] = d3.pointer(event, this);
    const bisectDate = d3.bisector((d) => d.Date).left;
    const activeData = toggleYear ? dataYear : dataMonth;
    if (!activeData.length) return;
    const x0 = xTimeAxis.invert(xCoord);
    const i = Math.min(bisectDate(activeData, x0, 1), activeData.length - 1);
    const d0 = activeData[i - 1];
    const d1 = activeData[i];
    const d = !d0 ? d1 : !d1 ? d0 : x0 - d0.Date > d1.Date - x0 ? d1 : d0;
    if (!d) return;
    const xPos = xTimeAxis(d.Date);
    const yPos = y(d.Count);

    if (!isNaN(xPos) && !isNaN(yPos)) {
      // Update circle position
      circle.attr("cx", xPos).attr("cy", yPos);
      circle.transition().duration(50).attr("r", Math.max(5, 4 * typeScale));

      // Update the red lines extending from the circle
      tooltipLineX
        .style("display", "block")
        .attr("x1", xPos)
        .attr("x2", xPos)
        .attr("y1", 0)
        .attr("y2", height);
      tooltipLineY
        .style("display", "block")
        .attr("y1", yPos)
        .attr("y2", yPos)
        .attr("x1", 0)
        .attr("x2", width);

      // Update the position and content of the tooltips
      tooltip
        .style("opacity", 0.9)
        .style("left", `${event.pageX + 10}px`)
        .style("top", `${event.pageY - 28}px`)
        .html(
          `(${
            d.Date !== undefined
              ? d3.timeFormat(toggleYear ? "%Y" : "%Y-%m")(d.Date)
              : "N/A"
          }, ${d.Count !== undefined ? d.Count : "N/A"})`,
        );
    }
  });

  listeningRect.on("mouseleave", function () {
    circle.transition().duration(50).attr("r", 0);
    tooltip.style("opacity", 0);
    tooltipRawDate.style("opacity", 0);
    tooltipLineX.style("display", "none");
    tooltipLineY.style("display", "none");
  });

  // Define the slider with years as integers
  var sliderRange = d3
    .sliderBottom()
    .min(minYear)
    .max(maxYear)
    .width(width)
    .tickFormat(d3.format("d")) // Display years as integers
    .ticks(Math.max(4, Math.min(12, Math.round(width / 140))))
    .default([minYear, maxYear])
    .fill("#55caff");

  sliderRange.on("onchange", (val) => {
    const [startYear, endYear] = val;

    // Convert years to Date objects
    const startDate = new Date(startYear, 0, 1);
    const endDate = new Date(endYear, 0, 1);

    xTimeAxis.domain([startDate, endDate]);

    let filteredData;
    if (endDate - startDate <= 86400000 * 365 * 10) {
      // 10 years in milliseconds
      toggleYear = false;
      filteredData = dataMonth.filter(
        (d) => d.Date >= startDate && d.Date <= endDate,
      );
    } else {
      toggleYear = true;
      filteredData = dataYear.filter(
        (d) => d.Date >= startDate && d.Date <= endDate,
      );
    }

    if (
      filteredData.length > 0 &&
      filteredData.every((d) => d.Date && d.Count != null)
    ) {
      g.select(".line")
        .transition()
        .duration(300)
        .attr("d", line(filteredData));
      g.select(".area")
        .transition()
        .duration(300)
        .attr("d", area(filteredData));
      y.domain([0, d3.max(filteredData, (d) => d.Count)]);

      g.select(".x-axis")
        .transition()
        .duration(300)
        .call(
          d3
            .axisBottom(xTimeAxis)
            .ticks(xTickCount)
            .tickFormat(
              toggleYear ? d3.timeFormat("%Y") : d3.timeFormat("%Y-%m"),
            ),
        );
      axisLabel.text(toggleYear ? "Year" : "Month");

      g.select(".y-axis")
        .transition()
        .duration(300)
        .call(
          d3
            .axisRight(y)
            .ticks(yTickCount)
            .tickFormat((d) => (d > 0 ? d : "")),
        );
    }
  });

  const gRange = svg
    .append("g")
    .attr("width", width)
    .attr("height", sliderReserved)
    .append("g")
    .attr(
      "transform",
      `translate(${margin.left},${margin.top + height + margin.bottom - 8})`,
    );

  gRange.call(sliderRange);

  if (typeof addDownloadButtonToChart === "function" && downloadable)
    addDownloadButtonToChart("time-series", containerID);
}
